import styled from '@emotion/styled'
import { AppCommandBar } from 'app/AppCommandBar'
import { AutoStack, FlexSpinner, onFormatDate } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { MailboxForm, MailboxFormMode } from 'components/Mailbox/MailboxForm'
import { StickyPane } from 'components/StickyPane'
import { Box } from 'grid'
import { useCommand } from 'hooks/useCommand'
import { uniq } from 'lodash'
import {
  MailTemplate,
  MailTemplateType,
  ParticipantChange,
  ParticipantChangeType,
  ParticipantChangeUpdate,
  UserRole,
} from 'model'
import {
  ContextualMenuItemType,
  Dropdown,
  IDropdownOption,
  Label,
  Pivot,
  PivotItem,
  Text,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useParticipantChangesQuery,
  useTaxMailTemplatesQuery,
  useUpdateParticipantChangesMutation,
} from './TaxRequirements.generated'
import { TaxRequirementsList } from './TaxRequirementsList'

const Container = styled(Box)`
  box-shadow: 0 1.6px 3.6px 0 rgba(0, 0, 0, 0.132),
    0 0.3px 0.9px 0 rgba(0, 0, 0, 0.108);
  margin-bottom: 4px;
`

// enum ChangesView {
//   sent,
//   notSent
// }

export const TaxRequirementsView: React.FC = () => {
  // const [currentView, setCurrentView] = React.useState(ChangesView.notSent)
  const { t } = useTranslation()
  const [updateParticipantChanges] = useUpdateParticipantChangesMutation()

  // const defaultDateFrom = moment()
  //   .add(-1, 'month')
  //   .startOf('month')
  //   .toDate()
  // const defaultDateTo = moment()
  //   .startOf('day')
  //   .toDate()
  // const [selectedDateFrom, setSelectedDateFrom] = React.useState(
  //   defaultDateFrom
  // )
  // const [selectedDateTo, setSelectedDateTo] = React.useState(defaultDateTo)
  const { data, loading, error, refetch } = useParticipantChangesQuery()
  const participantChanges = ((data && data.participantChanges) ||
    []) as ParticipantChange[]

  const provingGrounds = data && data.provingGrounds ? data.provingGrounds : []
  const defaultPg = provingGrounds[0] ? provingGrounds[0]._id : ''
  const [selectedPg, setSelectedPg] = React.useState(defaultPg)
  const provingGroundOptions = provingGrounds.map((pg) => ({
    key: pg._id,
    text: `${pg.title} (${pg.abbr})`,
  })) as IDropdownOption[]

  const { data: templData, loading: templLoading } = useTaxMailTemplatesQuery({
    variables: { provingGroundId: selectedPg || defaultPg },
  })
  const mailTemplates =
    templData && templData.mailTemplates ? templData.mailTemplates : []

  const taxOfficeMailTemplates = mailTemplates.filter(
    (mt) =>
      mt.type === MailTemplateType.TaxOfficeChanges &&
      mt.provingGroundId === (selectedPg || defaultPg)
  )
  const requirementsMailTemplates = mailTemplates.filter(
    (mt) =>
      mt.type === MailTemplateType.TaxRequirements &&
      mt.provingGroundId === (selectedPg || defaultPg)
  )

  const participantChangesForPG = participantChanges.filter(
    (pc) => pc.proving.provingGround!._id === (selectedPg || defaultPg)
  )

  const participantChangeItemsNotSent = participantChangesForPG.filter(
    (pc) => !pc.isSentToTaxOffice || !pc.isSentToInvolvedPeople
  )
  const participantChangeItemsSent = participantChangesForPG.filter(
    (pc) => pc.isSentToTaxOffice && pc.isSentToInvolvedPeople
  )

  let mailAddresses: string[] = []
  participantChangeItemsNotSent.forEach((pc) => {
    if (pc.type === ParticipantChangeType.Added) {
      if (!pc.isSentToInvolvedPeople) {
        if (pc.user && pc.user.email) {
          mailAddresses.push(pc.user.email)
        }
        if (pc.proving.responsible) {
          pc.proving.responsible.forEach((resp) => {
            if (resp.email) {
              mailAddresses.push(resp.email)
            }
          })
        }
      }
    }
  })
  mailAddresses = uniq(mailAddresses)

  const TaxRequirementsCommands: React.FC<{
    requirementsTemplates: MailTemplate[]
    taxOfficeTemplates: MailTemplate[]
    activeParticipantChanges: ParticipantChange[]
  }> = ({
    requirementsTemplates,
    taxOfficeTemplates,
    activeParticipantChanges,
  }) => {
    const [requirementsMailOpen, setRequirementsMailOpen] =
      React.useState(false)
    const [taxOfficeMailOpen, setTaxOfficeMailOpen] = React.useState(false)
    const [selectedTemplate, setSelectedTemplate] =
      React.useState<MailTemplate>()
    const getChangesString = () => {
      let changesToRender: ParticipantChange[] = []
      changesToRender = activeParticipantChanges.filter(
        (pc) => !pc.isSentToTaxOffice
      )
      return `<table><tr><th>Name</th><th>Abteilung</th><th>P-Nummer</th><th>ERP-Nummer</th><th>Von</th><th>Bis</th><th>Anmerkung</th></tr>${changesToRender.reduce(
        (prev, curr) => {
          let color: string = ''
          switch (curr.type) {
            case ParticipantChangeType.Added:
              color = '#00CB2B'
              break
            case ParticipantChangeType.Removed:
              color = '#CB0000'
              break
            case ParticipantChangeType.ChangedDateTo:
              color = '#000000'
              break
            case ParticipantChangeType.ChangedDateFrom:
              color = '#000000'
              break
            default:
              color = '#FFF'
              break
          }
          const dateFrom = onFormatDate(new Date(Date.parse(curr.dateFrom)))
          const dateTo = onFormatDate(new Date(Date.parse(curr.dateTo)))
          const oldDateFrom = onFormatDate(
            new Date(Date.parse(curr.oldDateFrom))
          )
          const oldDateTo = onFormatDate(new Date(Date.parse(curr.oldDateTo)))
          const dateFromComponent =
            curr.type === ParticipantChangeType.ChangedDateFrom
              ? `<s>${oldDateFrom}</s> ${dateFrom}`
              : dateFrom
          const dateToComponent =
            curr.type === ParticipantChangeType.ChangedDateTo
              ? `<s>${oldDateTo}</s> ${dateTo}`
              : dateTo
          return (
            prev +
            `<tr style="color: ${color};"><td>${curr.fullName}</td><td>${
              curr.department ? curr.department : ''
            }</td><td>${curr.employeeId ? curr.employeeId : ''}</td><td>${
              curr.proving.id
            }</td><td>${dateFromComponent}</td><td>${dateToComponent}</td><td>${t(
              `enum.participantChangeType.${curr.type}`
            )}</td></tr>`
          )
        },
        ''
      )}</table>`
    }
    const onRequirementsMailSent = React.useCallback(async () => {
      const updates: ParticipantChangeUpdate[] = activeParticipantChanges
        .filter((pc) => !pc.isSentToInvolvedPeople)
        .map((pc) => ({
          _id: pc._id,
          isSentToTaxOffice: pc.isSentToTaxOffice,
          isSentToInvolvedPeople: true,
        }))

      await updateParticipantChanges({
        variables: {
          updates,
        },
      })
      await refetch()
      setRequirementsMailOpen(false)
    }, [activeParticipantChanges, setRequirementsMailOpen])
    const onTaxOfficeMailSent = React.useCallback(async () => {
      const updates: ParticipantChangeUpdate[] = activeParticipantChanges
        .filter((pc) => !pc.isSentToTaxOffice)
        .map((pc) => ({
          _id: pc._id,
          isSentToTaxOffice: true,
          isSentToInvolvedPeople: pc.isSentToInvolvedPeople,
        }))

      await updateParticipantChanges({
        variables: {
          updates,
        },
      })
      await refetch()
      setTaxOfficeMailOpen(false)
    }, [activeParticipantChanges, setTaxOfficeMailOpen])
    useCommand(
      {
        key: 'mailPersons',
        text: 'E-Mail an betroffene Personen versenden',
        iconProps: { iconName: 'Mail' },
        subMenuProps: {
          items: [
            {
              key: 'selectTaxRequirementsTemplate',
              itemType: ContextualMenuItemType.Header,
              text: 'Vorlage auswählen',
            },
            ...requirementsTemplates.map((reqTempl) => ({
              key: reqTempl._id,
              text: reqTempl.name,
              onClick: () => {
                setRequirementsMailOpen(true)
                setSelectedTemplate(reqTempl)
              },
            })),
          ],
        },
      },
      [setSelectedTemplate, setRequirementsMailOpen]
    )
    useCommand(
      {
        key: 'mailTaxOffice',
        text: 'E-Mail an Steuerkanzlei versenden',
        iconProps: { iconName: 'Mail' },
        subMenuProps: {
          items: [
            {
              key: 'selectTaxOfficeTemplate',
              itemType: ContextualMenuItemType.Header,
              text: 'Vorlage auswählen',
            },
            ...taxOfficeTemplates.map((taxTempl) => ({
              key: taxTempl._id,
              text: taxTempl.name,
              onClick: () => {
                setTaxOfficeMailOpen(true)
                setSelectedTemplate(taxTempl)
              },
            })),
          ],
        },
      },
      [setSelectedTemplate, setTaxOfficeMailOpen]
    )
    return (
      <>
        <AppCommandBar />
        {selectedTemplate && (
          <>
            <MailboxForm
              isOpen={requirementsMailOpen}
              mode={MailboxFormMode.New}
              initialMail={{
                to: mailAddresses,
                cc: [],
                subject: selectedTemplate.subject || '',
                body: selectedTemplate.body || '',
              }}
              onDismiss={() => {
                setRequirementsMailOpen(false)
                setSelectedTemplate(undefined)
              }}
              onSaved={onRequirementsMailSent}
              canSaveAsDraft={false}
            />
            <MailboxForm
              isOpen={taxOfficeMailOpen}
              mode={MailboxFormMode.New}
              initialMail={{
                to: selectedTemplate.to ? selectedTemplate.to.split(';') : [],
                cc: selectedTemplate.cc ? selectedTemplate.cc.split(';') : [],
                subject: selectedTemplate.subject || '',
                body: selectedTemplate.body
                  ? selectedTemplate.body.replace(
                      '{%Liste%}',
                      getChangesString()
                    )
                  : '',
              }}
              onDismiss={() => {
                setTaxOfficeMailOpen(false)
                setSelectedTemplate(undefined)
              }}
              onSaved={onTaxOfficeMailSent}
              canSaveAsDraft={false}
            />
          </>
        )}
      </>
    )
  }

  return (
    <ViewAuthorizer
      roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
    >
      <StickyPane>
        <FlexSpinner loading={loading || templLoading} error={error}>
          <TaxRequirementsCommands
            taxOfficeTemplates={taxOfficeMailTemplates}
            requirementsTemplates={requirementsMailTemplates}
            activeParticipantChanges={participantChangeItemsNotSent}
          />
          <Container>
            <AutoStack
              horizontal={true}
              horizontalAlign={'space-between'}
              tokens={{ childrenGap: 8 }}
              styles={{
                root: {
                  padding: 8,
                },
              }}
            >
              <Text variant="xLarge" as="h2">
                {'Änderungen Teilnehmer'}
              </Text>
              <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                {/* <Label>Von</Label>
                <DatePicker
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  value={selectedDateFrom}
                  onSelectDate={value => {
                    if (value) {
                      setSelectedDateFrom(value)
                    }
                  }}
                  formatDate={onFormatDate as any}
                />
                <Label>Bis</Label>
                <DatePicker
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  value={selectedDateTo}
                  onSelectDate={value => {
                    if (value) {
                      setSelectedDateTo(value)
                    }
                  }}
                  formatDate={onFormatDate as any}
                /> */}
                <Label>Standort</Label>
                <Dropdown
                  options={provingGroundOptions}
                  defaultSelectedKey={selectedPg || defaultPg}
                  styles={{
                    root: { width: 300 },
                  }}
                  onChange={(_, val) => {
                    if (val) {
                      setSelectedPg(val.key as string)
                    }
                  }}
                />
              </AutoStack>
            </AutoStack>
          </Container>
          <Pivot>
            <PivotItem
              headerText="Noch nicht versendet"
              headerButtonProps={{ 'data-order': 1 }}
            >
              <TaxRequirementsList items={participantChangeItemsNotSent} />
            </PivotItem>
            <PivotItem
              headerText="Bereits versendet"
              headerButtonProps={{ 'data-order': 2 }}
            >
              <TaxRequirementsList items={participantChangeItemsSent} />
            </PivotItem>
          </Pivot>
        </FlexSpinner>
      </StickyPane>
    </ViewAuthorizer>
  )
}
